import { useSWRConfig } from 'swr';
import { MutatorCallback } from 'swr/dist/types';

/**
 * returns a function that receives a regex expression as key
 * and be used to mutate the ones who matched this pattern.
 *
 * @example mutate(new RegExp(/^\/api\//), data, false)
 *
 * @see {@link https://swr.vercel.app/docs/advanced/cache#mutate-multiple-keys-from-regex}
 */
export const useMutateMatchingRegex = () => {
  const { cache, mutate } = useSWRConfig();

  /**
   * Function to be used to match an existing key store on the SWR cache.
   * If it matches the regex, it will execute the mutation
   *
   * @example mutate(new RegExp(/^\/api\//), data, false)
   */
  return <T>(args: {
    matcher: RegExp;
    data?: T | Promise<T> | MutatorCallback<T>;
    shouldRevalidate?: boolean;
  }): Promise<T[] | void> => {
    try {
      /** SWRConfig lets you add a provider, this provider needs to be
       * instantiated with `new Map().` Right now we dont expose this API so this
       * is a safety net just in case it gets added
       * @see {@link https://swr.vercel.app/docs/advanced/cache#create-cache-provider}
       */
      if (!(cache instanceof Map)) {
        return Promise.reject(
          new Error(
            'matchMutate requires the cache provider to be a Map instance',
          ),
        );
      }

      const keys = [];

      /**
       * on ts-config, the property "downlevelIteration: true" omits this error message:
       * 'IterableIterator' is not an array type or a string type.
       * Use compiler option '--downlevelIteration' to allow iterating
       * of iterators.
       */
      for (const key of cache.keys()) {
        if (args.matcher.test(key)) {
          keys.push(key);
        }
      }

      // Bail out if there is nothing to match
      if (keys.length === 0) {
        return Promise.resolve();
      }

      const mutations = keys.map((key) =>
        mutate(key, args.data, args.shouldRevalidate),
      );

      return Promise.all(mutations);
    } catch (e) {
      return Promise.reject(e);
    }
  };
};
